interface MediaEntry {
  url: string;
}

interface MediaFlight {
  controller: AbortController;
  promise: Promise<string>;
  consumers: number;
  isSettled: boolean;
}

export interface MediaCacheOptions {
  maxEntries: number;
  load(key: string, signal: AbortSignal): Promise<Blob>;
  createObjectURL?: (blob: Blob) => string;
  revokeObjectURL?: (url: string) => void;
}

function abortError(): DOMException {
  return new DOMException("media request aborted", "AbortError");
}

export function createMediaCache(options: MediaCacheOptions) {
  if (!Number.isSafeInteger(options.maxEntries) || options.maxEntries < 1) {
    throw new Error("media cache maxEntries must be a positive integer");
  }
  const createObjectURL = options.createObjectURL ?? URL.createObjectURL;
  const revokeObjectURL = options.revokeObjectURL ?? URL.revokeObjectURL;
  const entries = new Map<string, MediaEntry>();
  const flights = new Map<string, MediaFlight>();
  const heldKeys = new Map<string, number>();
  let generation = 0;

  function remove(key: string): void {
    const entry = entries.get(key);
    if (!entry) return;
    entries.delete(key);
    revokeObjectURL(entry.url);
  }

  function trim(): void {
    while (entries.size > options.maxEntries) {
      const candidate = [...entries.keys()].find((key) => !heldKeys.has(key));
      if (!candidate) return;
      remove(candidate);
    }
  }

  function retain(key: string, url: string): string {
    entries.delete(key);
    entries.set(key, { url });
    trim();
    return url;
  }

  async function consume(flight: MediaFlight, signal?: AbortSignal): Promise<string> {
    if (signal?.aborted) throw abortError();
    flight.consumers += 1;
    let onAbort = () => {};
    const aborted = new Promise<never>((_resolve, reject) => {
      if (!signal) return;
      onAbort = () => reject(abortError());
      signal.addEventListener("abort", onAbort, { once: true });
    });
    try {
      return signal ? await Promise.race([flight.promise, aborted]) : await flight.promise;
    } finally {
      signal?.removeEventListener("abort", onAbort);
      flight.consumers -= 1;
      if (flight.consumers === 0 && !flight.isSettled) flight.controller.abort();
    }
  }

  return {
    async get(key: string, signal?: AbortSignal): Promise<string> {
      const cached = entries.get(key);
      if (cached) return retain(key, cached.url);

      let flight = flights.get(key);
      if (flight?.controller.signal.aborted) {
        flights.delete(key);
        flight = undefined;
      }
      if (!flight) {
        const controller = new AbortController();
        const flightGeneration = generation;
        let created: MediaFlight;
        const promise = options
          .load(key, controller.signal)
          .then((blob) => {
            if (controller.signal.aborted || flightGeneration !== generation) throw abortError();
            const url = createObjectURL(blob);
            if (controller.signal.aborted || flightGeneration !== generation) {
              revokeObjectURL(url);
              throw abortError();
            }
            return retain(key, url);
          })
          .finally(() => {
            created.isSettled = true;
            if (flights.get(key) === created) flights.delete(key);
          });
        created = { controller, promise, consumers: 0, isSettled: false };
        flights.set(key, created);
        flight = created;
      }
      return await consume(flight, signal);
    },
    peek(key: string): string | null {
      return entries.get(key)?.url ?? null;
    },
    hold(key: string): () => void {
      heldKeys.set(key, (heldKeys.get(key) ?? 0) + 1);
      let isReleased = false;
      return () => {
        if (isReleased) return;
        isReleased = true;
        const remaining = (heldKeys.get(key) ?? 1) - 1;
        if (remaining > 0) heldKeys.set(key, remaining);
        else heldKeys.delete(key);
        trim();
      };
    },
    clear(): void {
      generation += 1;
      for (const flight of flights.values()) flight.controller.abort();
      flights.clear();
      for (const key of [...entries.keys()]) remove(key);
      heldKeys.clear();
    },
  };
}

export type MediaCache = ReturnType<typeof createMediaCache>;
